import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Switch,
  Typography,
  Modal,
  Empty,
  Alert,
  Progress,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchTriggers,
  deleteTrigger,
  updateTrigger,
  createTrigger,
  selectTriggers,
  selectTemplates,
  selectSegments,
  selectPushLoading,
} from 'store/slices/push.slice';
import { usePermissions } from 'hooks/usePermissions';
import { EventPushTrigger } from 'types/push_management.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import EventTriggerLoader, { LoadedTrigger } from 'components/push/EventTriggerLoader';

const { confirm } = Modal;
const { Text } = Typography;

interface Props {
  appId: number | null;
}
interface ImportState {
  active: boolean;
  total: number;
  done: number;
  errors: string[];
}
const IMPORT_IDLE: ImportState = { active: false, total: 0, done: 0, errors: [] };
const EventTriggerList: React.FC<Props> = ({ appId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();

  const triggers = useAppSelector(selectTriggers);
  const templates = useAppSelector(selectTemplates);
  const segments = useAppSelector(selectSegments);
  const loading = useAppSelector(selectPushLoading);

  const [importState, setImportState] = useState<ImportState>(IMPORT_IDLE);
  const handleLoad = async (loaded: LoadedTrigger[], templateName?: string, _targetLang?: string) => {
    if (!appId) return;

    setImportState({ active: true, total: loaded.length, done: 0, errors: [] });

    const errors: string[] = [];

    for (let i = 0; i < loaded.length; i++) {
      const t = loaded[i];
      try {
        await dispatch(createTrigger({
          app_id: appId,
          name: t.name,
          trigger_type: t.trigger_type,
          push_template_id: t.push_template_id,
          segment_id: t.segment_id,
          campaign_id: t.campaign_id,
          delay_minutes: t.delay_minutes,
          is_active: t.is_active,
          max_sends_per_user: t.max_sends_per_user,
          cooldown_hours: t.cooldown_hours,
        })).unwrap();
      } catch (err: any) {
        errors.push(`"${t.name}": ${err?.message ?? 'неизвестная ошибка'}`);
      }

      setImportState(prev => ({ ...prev, done: i + 1, errors }));
    }

    await dispatch(fetchTriggers(appId));

    const successCount = loaded.length - errors.length;

    if (errors.length === 0) {
      notify.success(
        `Импортировано ${successCount} триггеров${templateName ? ` из "${templateName}"` : ''}`
      );
      setImportState(IMPORT_IDLE);
    } else {
      setImportState(prev => ({ ...prev, active: false }));
      if (successCount > 0) {
        notify.success(`Импортировано ${successCount} из ${loaded.length} триггеров`);
      }
    }
  };

  const dismissImportErrors = () => setImportState(IMPORT_IDLE);
  const handleToggleActive = async (trigger: EventPushTrigger) => {
    try {
      await dispatch(updateTrigger({
        id: trigger.id,
        data: { is_active: !trigger.is_active },
      })).unwrap();
      notify.success(
        trigger.is_active ? 'Триггер деактивирован' : 'Триггер активирован'
      );
      if (appId) dispatch(fetchTriggers(appId));
    } catch (error: any) {
      notify.error(error.message || 'Ошибка при обновлении триггера');
    }
  };

  const handleDelete = (trigger: EventPushTrigger) => {
    confirm({
      title: 'Удалить триггер?',
      content: `Вы уверены, что хотите удалить триггер "${trigger.name}"?`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await dispatch(deleteTrigger(trigger.id)).unwrap();
          notify.success('Триггер успешно удален');
        } catch (error: any) {
          notify.error(error.message || 'Ошибка при удалении');
        }
      },
    });
  };

  const getTriggerTypeTag = (type: string) => {
    const config: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
      install:       { color: 'blue',   text: 'Установка',      icon: <UserOutlined /> },
      registration:  { color: 'green',  text: 'Регистрация',    icon: <UserOutlined /> },
      deposit:       { color: 'gold',   text: 'Депозит',        icon: <UserOutlined /> },
      first_deposit: { color: 'orange', text: 'Первый депозит', icon: <UserOutlined /> },
    };
    const { color, text, icon } = config[type] || { color: 'default', text: type, icon: null };
    return <Tag color={color} icon={icon}>{text}</Tag>;
  };

  const formatDelay = (minutes: number) => {
    if (minutes === 0) return 'Сразу';
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} ч`;
    return `${hours} ч ${mins} мин`;
  };

  const getTemplateName = (templateId: number) =>
    templates.find(t => t.id === templateId)?.name || 'Неизвестный шаблон';

  const getSegmentName = (segmentId?: number) => {
    if (!segmentId) return 'Все пользователи';
    return segments.find(s => s.id === segmentId)?.name || 'Неизвестный сегмент';
  };

  const columns: ColumnsType<EventPushTrigger> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Шаблон: {getTemplateName(record.push_template_id)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Событие',
      dataIndex: 'trigger_type',
      key: 'trigger_type',
      width: 160,
      render: (type: string) => getTriggerTypeTag(type),
    },
    {
      title: 'Задержка',
      dataIndex: 'delay_minutes',
      key: 'delay_minutes',
      width: 120,
      render: (minutes: number) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#999' }} />
          <Text>{formatDelay(minutes)}</Text>
        </Space>
      ),
    },
    {
      title: 'Аудитория',
      key: 'audience',
      width: 180,
      render: (_, record) => (
        <Text type={record.segment_id ? undefined : 'secondary'}>
          {getSegmentName(record.segment_id)}
        </Text>
      ),
    },
    {
      title: 'Лимиты',
      key: 'limits',
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>Макс: {record.max_sends_per_user} раз</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>Cooldown: {record.cooldown_hours}ч</Text>
        </Space>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive: boolean, record) => (
        <Tooltip title={isActive ? 'Деактивировать' : 'Активировать'}>
          <Switch
            checked={isActive}
            onChange={() => handleToggleActive(record)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
            disabled={!permissions.canManagePush}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date: string) => formatters.date(date),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(ROUTES.EVENT_TRIGGER_EDIT.replace(':id', String(record.id)))}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (!appId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Выберите приложение для просмотра триггеров"
      />
    );
  }

  const importProgress = importState.total > 0
    ? Math.round((importState.done / importState.total) * 100)
    : 0;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {triggers.length > 0 ? `${triggers.length} триггеров` : 'Нет триггеров'}
        </Text>
        <Space>
          <EventTriggerLoader
            disabled={!appId || !permissions.canManagePush}
            appId={appId ?? undefined}
            segments={segments}
            templates={templates}
            onLoad={handleLoad}
          />
        </Space>
      </div>

      {importState.active && (
        <Alert
          type="info"
          showIcon
          message={
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text>Импорт триггеров: {importState.done} / {importState.total}</Text>
              <Progress percent={importProgress} size="small" status="active" style={{ marginBottom: 0 }} />
            </Space>
          }
        />
      )}

      {!importState.active && importState.errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={dismissImportErrors}
          message={`Не удалось импортировать ${importState.errors.length} триггеров`}
          description={
            <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
              {importState.errors.map((e, i) => (
                <li key={i} style={{ fontSize: 12 }}>{e}</li>
              ))}
            </ul>
          }
        />
      )}

      <Table
        columns={columns}
        dataSource={triggers}
        loading={loading || importState.active}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Всего: ${total}`,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" align="center">
                  <Text>Триггеры не найдены</Text>
                </Space>
              }
            />
          ),
        }}
        scroll={{ x: 1100 }}
      />
    </Space>
  );
};

export default EventTriggerList;